import { COMPANY } from './company'
import { ROUTES } from './routes'
import type { FaqGroup } from './content-pages'

export const FAQ_PAGE = {
  eyebrow: 'Help centre',
  title: 'Frequently asked questions',
  lead: 'Answers about orders, delivery, flowers, plants, corporate services, payments, and partnerships.',
  groups: [
    {
      title: 'General questions',
      items: [
        {
          question: 'What is Maaliondoor?',
          answer:
            'Maaliondoor is a one-stop platform offering flowers, plants, corporate gifting, plant rental, landscaping, and event decoration services for individuals and businesses across India.',
        },
        {
          question: 'Where is Maaliondoor located?',
          answer: `Our registered office is: ${COMPANY.address}`,
        },
        {
          question: 'Which locations do you serve?',
          answer:
            'We provide services across Delhi NCR and selected cities across India. Corporate gifting and bulk decoration services are available in multiple locations.',
        },
      ],
    },
    {
      title: 'Orders & delivery',
      items: [
        {
          question: 'How can I place an order?',
          answer: 'You can place an order through:',
          bullets: [
            `Our website: ${COMPANY.websiteLabel}`,
            `Phone: ${COMPANY.phoneDisplay}`,
            `Email: ${COMPANY.email}`,
          ],
        },
        {
          question: 'Do you offer same-day delivery?',
          answer:
            'Yes. Same-day delivery is available for selected products and locations, subject to order cut-off time and availability.',
        },
        {
          question: 'Can I schedule my delivery?',
          answer:
            'Yes. You can choose your preferred delivery date and time during checkout, subject to availability.',
        },
        {
          question: 'What happens if the recipient is unavailable?',
          answer:
            'Our delivery team will attempt to contact the recipient. If delivery cannot be completed due to recipient unavailability or incorrect details, additional delivery charges may apply for re-delivery.',
        },
      ],
    },
    {
      title: 'Flowers & plants',
      items: [
        {
          question: 'Will the flowers look exactly like the website images?',
          answer:
            'Flowers are natural products, so slight variations in color, size, wrapping, and arrangement may occur depending on seasonal availability.',
        },
        {
          question: 'Can I customize my bouquet?',
          answer:
            'Yes. We offer customized bouquets, floral arrangements, and gift hampers based on your preferences.',
        },
        {
          question: 'Do you offer indoor and outdoor plants?',
          answer:
            'Yes. We provide a wide range of indoor plants, outdoor plants, air-purifying plants, and corporate plant solutions.',
        },
        {
          question: 'Do you provide plant rental services?',
          answer:
            'Yes. We offer plant rental and maintenance services for offices, hotels, hospitals, events, and commercial spaces.',
        },
      ],
    },
    {
      title: 'Corporate services',
      items: [
        {
          question: 'Do you provide corporate gifting?',
          answer: 'Yes. We specialize in:',
          bullets: [
            'Employee welcome kits',
            'Festive gifts',
            'Client gifts',
            'Customized gift hampers',
            'Promotional gifts',
            'Bulk corporate orders',
          ],
        },
        {
          question: 'Do you handle office and event decoration?',
          answer: 'Yes. We provide professional decoration services for:',
          bullets: [
            'Office spaces',
            'Sales galleries',
            'Sample flats',
            'Corporate events',
            'Product launches',
            'Independence Day',
            'Republic Day',
            'Diwali',
            'Annual functions',
          ],
        },
        {
          question: 'Can you handle bulk orders?',
          answer:
            'Yes. We specialize in bulk orders for corporate gifting, events, festivals, and employee engagement programs.',
        },
      ],
    },
    {
      title: 'Payments',
      items: [
        {
          question: 'Which payment methods do you accept?',
          answer: 'We accept:',
          bullets: ['UPI', 'Credit cards', 'Debit cards', 'Net banking', 'Wallets', 'Bank transfer'],
        },
        {
          question: 'Is GST included in the price?',
          answer:
            'GST is charged as applicable under Indian tax laws and will be clearly mentioned on your invoice.',
        },
      ],
    },
    {
      title: 'Cancellation & refunds',
      items: [
        {
          question: 'Can I cancel my order?',
          answer:
            'Yes. Orders can be cancelled within the cancellation window as mentioned in our Refund & Returns Policy. Orders that are customized, prepared, or dispatched may not be eligible for cancellation.',
        },
        {
          question: 'What if I receive a damaged or incorrect product?',
          answer:
            'Please contact us within 2 hours of delivery with photographs of the product. We will review your request and arrange a replacement or refund if applicable.',
        },
      ],
    },
    {
      title: 'Quality & support',
      items: [
        {
          question: 'How do you ensure product quality?',
          answer:
            'We work with trusted florists, nurseries, and vendors and use premium-quality flowers, plants, and gifting materials to ensure customer satisfaction.',
        },
        {
          question: 'How can I contact customer support?',
          answer: 'You can reach us through:',
          bullets: [
            `Phone: ${COMPANY.phoneDisplay}`,
            `Email: ${COMPANY.email}`,
            `Website: ${COMPANY.websiteLabel}`,
          ],
        },
      ],
    },
    {
      title: 'Business partnerships',
      items: [
        {
          question: 'Can I become a Maaliondoor vendor?',
          answer:
            'Yes. We welcome florists, nurseries, gift suppliers, decorators, and other businesses to partner with us. Please contact us for vendor registration.',
        },
        {
          question: 'Do you offer franchise or business opportunities?',
          answer:
            'Yes. We are expanding our network and welcome partnership and business collaboration opportunities. Contact our team for more details.',
        },
      ],
    },
    {
      title: 'Additional information',
      items: [
        {
          question: 'Do you provide customized branding on corporate gifts?',
          answer:
            'Yes. We offer custom branding, logo printing, personalized packaging, and tailored gifting solutions for corporate clients.',
        },
        {
          question: 'Why should I choose Maaliondoor?',
          answer: '',
          bullets: [
            'Premium quality products',
            'Customized gifting solutions',
            'Professional event decoration',
            'Trusted local vendor network',
            'Competitive pricing',
            'Corporate & bulk order expertise',
            'Reliable customer support',
            'Timely delivery',
            'One-stop solution for flowers, plants & gifting',
          ],
        },
      ],
    },
  ] satisfies FaqGroup[],
  stillTitle: 'Still have questions?',
  stillLead: "We're here to help.",
  returnsLinkLabel: 'Read the refund & returns policy',
  returnsTo: ROUTES.returns,
  vendorTo: ROUTES.vendorRegistration,
  vendorLinkLabel: 'Become a vendor',
} as const
